// Copyright 2021 The Casdoor Authors. All Rights Reserved.
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//      http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import React, { Suspense, lazy } from "react";
import {
  Button,
  Checkbox,
  Col,
  Form,
  Input,
  Modal,
  Result,
  Row,
  Spin,
  Tabs,
  Typography,
  message,
  Divider,
} from "antd";
import {
  ArrowLeftOutlined,
  CheckCircleFilled,
  // LockOutlined
  // UserOutlined
} from "@ant-design/icons";
import { withRouter } from "react-router-dom";
import * as UserWebauthnBackend from "../backend/UserWebauthnBackend";
import OrganizationSelect from "../common/select/OrganizationSelect";
import * as Conf from "../Conf";
import * as AuthBackend from "./AuthBackend";
import * as OrganizationBackend from "../backend/OrganizationBackend";
import * as ApplicationBackend from "../backend/ApplicationBackend";
import * as Provider from "./Provider";
import * as Util from "./Util";
import * as Setting from "../Setting";
import * as AgreementModal from "../common/modal/AgreementModal";
import SelfLoginButton from "./SelfLoginButton";
import i18next from "i18next";
import CustomGithubCorner from "../common/CustomGithubCorner";
import { SendCodeInput } from "../common/SendCodeInput";
import LanguageSelect from "../common/select/LanguageSelect";
import { CaptchaModal, CaptchaRule } from "../common/modal/CaptchaModal";
import RedirectForm from "../common/RedirectForm";
import {
  MfaAuthVerifyForm,
  NextMfa,
  RequiredMfa,
} from "./mfa/MfaAuthVerifyForm";
import { GoogleOneTapLoginVirtualButton } from "./GoogleLoginButton";
import * as ProviderButton from "./ProviderButton";
import { customDict, sloganDict } from "../CustomDict";
import * as UserBackend from "../backend/UserBackend";
import { OssUrl } from "../Setting";
import useTrackSend from "../component/Tracker/alayaTracker"; // {trackSend} from "../component/Tracker/alayaTracker";
import { getURLWithUtmParams, buildAgreementPreviewUrl } from "../utils/index";
const FaceRecognitionModal = lazy(() =>
  import("../common/modal/FaceRecognitionModal")
);

const { Paragraph } = Typography;
class LoginPage extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      classes: props,
      type: props.type,
      applicationName:
        props.applicationName ?? props.match?.params?.applicationName ?? null,
      owner: props.owner ?? props.match?.params?.owner ?? null,
      mode: props.mode ?? props.match?.params?.mode ?? null, // "signup" or "signin"
      msg: null,
      username: null,
      validEmailOrPhone: false,
      validEmail: false,
      enableCaptchaModal: CaptchaRule.Never,
      openCaptchaModal: false,
      openFaceRecognitionModal: false,
      verifyCaptcha: undefined,
      samlResponse: "",
      relayState: "",
      redirectUrl: "",
      isTermsOfUseVisible: false,
      termsOfUseContent: "",
      orgChoiceMode:
        new URLSearchParams(props.location?.search).get("orgChoiceMode") ??
        null,
      CusClientSecret: "",
      CusCaptchaImg: "",
      CusCaptchaType: "none",
      updateState: false,
      userID: "",
      logRes: "", // 登录返回信息
      formValues: {},
      agreementNotifications: [],
      agreementNoticeUserId: "",
      registrationDocuments: [],
      wechatQRMode: false,
      wechatQRProvider: null,
      wechatQRLoading: false,
      // hasPolicy: false,
      // hasPrivacy: false,
      localCallbackSent: false,
      localCallbackAppName: "",
    };

    if (
      this.state.type === "cas" &&
      props.match?.params.casApplicationName !== undefined
    ) {
      this.state.owner = props.match?.params?.owner;
      this.state.applicationName = props.match?.params?.casApplicationName;
    }

    localStorage.setItem("signinUrl", window.location.href);

    this.form = React.createRef();
    this.trackSend = useTrackSend({
      enableMixpanel: true,
      enableUMeng: true,
    });
  }

  componentDidMount() {
    if (this.getApplicationObj() === undefined) {
      if (this.state.type === "login" || this.state.type === "saml") {
        this.getApplication();
      } else if (this.state.type === "code" || this.state.type === "cas") {
        this.getApplicationLogin();
      } else {
        Setting.showMessage(
          "error",
          `Unknown authentication type: ${this.state.type}`
        );
      }
    }
    this.props.application && this.loadCaptcha(this.props.application);
    this.trackSend.trackSend({
      trackEventName: "sso_login_view",
    });
    this.getRegistrationDocuments();
    // 切换语言时 locale 请求头会变，需要重新拉取协议列表
    this.onLanguageChanged = () => this.getRegistrationDocuments();
    i18next.on("languageChanged", this.onLanguageChanged);
    // this.getFile();
  }

  componentWillUnmount() {
    i18next.off("languageChanged", this.onLanguageChanged);
  }

  getRegistrationDocuments() {
    AuthBackend.getRegistrationDocuments()
      .then((res) => {
        if (res?.code === 0 && Array.isArray(res.data) && res.data.length > 0) {
          this.setState({ registrationDocuments: res.data });
        }
      })
      .catch(() => {
        // 查询失败时保持 registrationDocuments 为空，渲染回退到静态 /view 链接
      });
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (
      prevState.loginMethod === undefined &&
      this.state.loginMethod === undefined
    ) {
      const application = this.getApplicationObj();
      this.setState({ loginMethod: this.resolveLoginMethod(application) });
    }
    if (prevProps.application !== this.props.application) {
      this.setState({
        loginMethod: this.resolveLoginMethod(this.props.application),
      });
      this.loadCaptcha(this.props.application);

      const captchaProviderItems = this.getCaptchaProviderItems(
        this.props.application
      );
      if (captchaProviderItems) {
        if (
          captchaProviderItems.some(
            (providerItem) => providerItem.rule === "Always"
          )
        ) {
          this.setState({ enableCaptchaModal: CaptchaRule.Always });
        } else if (
          captchaProviderItems.some(
            (providerItem) => providerItem.rule === "Dynamic"
          )
        ) {
          this.setState({ enableCaptchaModal: CaptchaRule.Dynamic });
        } else {
          this.setState({ enableCaptchaModal: CaptchaRule.Never });
        }
      }
    }

    if (
      prevProps.account !== this.props.account &&
      this.props.account !== undefined
    ) {
      if (
        this.props.account &&
        this.props.account.owner === this.props.application?.organization
      ) {
        const params = new URLSearchParams(this.props.location.search);
        const silentSignin = params.get("silentSignin");
        if (silentSignin !== null) {
          this.sendSilentSigninData("signing-in");

          const values = {};
          values["application"] = this.props.application.name;
          this.login(values);
        }

        if (params.get("popup") === "1") {
          window.addEventListener("beforeunload", () => {
            this.sendPopupData(
              { type: "windowClosed" },
              params.get("redirect_uri")
            );
          });
        }

        if (this.props.application.enableAutoSignin) {
          const values = {};
          values["application"] = this.props.application.name;
          this.login(values);
        }
      }
    }
    if (
      this.state.wechatQRMode &&
      !prevState.wechatQRMode &&
      this.state.wechatQRProvider
    ) {
      this.initWxLogin();
    }
  }

  initWxLogin() {
    const application = this.getApplicationObj();
    const params = Provider.getWxLoginParams(
      application,
      this.state.wechatQRProvider,
      "signup"
    );
    if (!params) {
      return;
    }
    this.setState({ wechatQRLoading: true });
    console.log("initWxLogin", params);
    Util.loadWxLoginScript()
      .then(() => {
        console.log("load success", params);
        new window.WxLogin({
          self_redirect: false,
          id: "wechat-qr-container",
          appid: params.appid,
          scope: params.scope,
          redirect_uri: params.redirect_uri,
          state: params.state,
          style: "black",
          href: "",
        });
        this.setState({ wechatQRLoading: false });
      })
      .catch(() => {
        this.setState({ wechatQRLoading: false });
        Setting.showMessage(
          "error",
          i18next.t("login:Failed to load WeChat QR code")
        );
      });
  }

  // getFile() {
  //   AuthBackend.downFile("policy").then(
  //     res => {
  //       this.setState({
  //         hasPolicy: res.byteLength > 0 ? true : false,
  //       });
  //     });
  //   AuthBackend.downFile("privacy").then(
  //     res => {
  //       this.setState({
  //         hasPrivacy: res.byteLength > 0 ? true : false,
  //       });
  //     });
  // }

  // 获取验证码
  loadCaptcha(application, isCurrentProvider = true) {
    const captchaProviderItems = this.getCaptchaProviderItems(application);
    const alwaysProviderItems = (captchaProviderItems || []).filter(
      (providerItem) => providerItem.rule === "Always"
    );
    const dynamicProviderItems = (captchaProviderItems || []).filter(
      (providerItem) => providerItem.rule === "Dynamic"
    );
    const provider =
      alwaysProviderItems.length > 0
        ? alwaysProviderItems[0]?.provider
        : dynamicProviderItems[0]?.provider;

    UserBackend.getCaptcha(
      provider?.owner,
      provider?.name,
      isCurrentProvider
    ).then((res) => {
      if (res?.type === "none") {
        this.setState({
          CusClientSecret: "",
          CusCaptchaImg: "",
          CusCaptchaType: "none",
        });
      } else if (res?.type === "Default") {
        this.setState({
          CusClientSecret: res.captchaId,
          CusCaptchaImg: res.captchaImage,
          CusCaptchaType: "Default",
        });
      } else {
        this.setState({
          CusClientSecret: res?.clientSecret,
          CusCaptchaImg: res?.captchaImage,
          CusCaptchaType: res?.type,
        });
        // setOpen(true);
        // setCaptchaType(res.type);
        // setClientId(res.clientId);
        // setClientSecret(res.clientSecret);
        // setSubType(res.subType);
        // setClientId2(res.clientId2);
        // setClientSecret2(res.clientSecret2);
      }
    });
  }

  checkCaptchaStatus(values) {
    AuthBackend.getCaptchaStatus(values).then((res) => {
      if (res.status === "ok") {
        if (res.data) {
          this.setState({
            openCaptchaModal: true,
            values: values,
          });
          return null;
        }
      }
      this.login(values);
    });
  }

  getApplicationLogin() {
    const loginParams =
      this.state.type === "cas"
        ? Util.getCasLoginParameters("admin", this.state.applicationName)
        : Util.getOAuthGetParameters();
    AuthBackend.getApplicationLogin(loginParams).then((res) => {
      if (res.status === "ok") {
        const application = res.data;
        this.onUpdateApplication(application);
      } else {
        this.onUpdateApplication(null);
        this.setState({
          msg: res.msg,
        });
      }
    });
  }

  getApplication() {
    if (this.state.applicationName === null) {
      return null;
    }

    if (this.state.owner === null || this.state.type === "saml") {
      ApplicationBackend.getApplication(
        "admin",
        this.state.applicationName
      ).then((res) => {
        if (res.status === "error") {
          this.onUpdateApplication(null);
          this.setState({
            msg: res.msg,
          });
          return;
        }
        this.onUpdateApplication(res.data);
      });
    } else {
      OrganizationBackend.getDefaultApplication("admin", this.state.owner).then(
        (res) => {
          if (res.status === "ok") {
            const application = res.data;
            this.onUpdateApplication(application);
            this.setState({
              applicationName: res.data.name,
            });
          } else {
            this.onUpdateApplication(null);
            Setting.showMessage("error", res.msg);

            this.props.history.push("/404");
          }
        }
      );
    }
  }

  getApplicationObj() {
    return this.props.application;
  }

  isPasswordLoginVisible(application) {
    // signinMethods 缺失时（存量应用）保持原密码登录行为
    if (!application?.signinMethods?.length) {
      return true;
    }
    return application.signinMethods.some((item) => item.name === "Password");
  }

  // All 规则展示邮箱或手机号验证码登录，Phone only 只展示手机号
  getCodeLoginMethod(application) {
    const item = application?.signinMethods?.find(
      (i) =>
        i.name === "Verification code" &&
        ["All", "Phone only"].includes(i.rule)
    );
    if (!item) {
      return null;
    }
    return item.rule === "All" ? "verificationCode" : "verificationCodePhone";
  }

  getPhoneCountryCode() {
    // 文档约定固定 CN，与 ProviderItem 的 CountryCodes=["CN"] 匹配
    return "CN";
  }

  resolveLoginMethod(application) {
    const passwordVisible = this.isPasswordLoginVisible(application);
    const codeLoginMethod = this.getCodeLoginMethod(application);
    let loginMethod = this.getDefaultLoginMethod(application);
    if (loginMethod?.includes("verificationCode")) {
      if (codeLoginMethod) {
        loginMethod = codeLoginMethod;
      } else if (passwordVisible) {
        loginMethod = "password";
      }
    } else if (loginMethod === "password" && !passwordVisible && codeLoginMethod) {
      loginMethod = codeLoginMethod;
    }
    return loginMethod;
  }

  getDefaultLoginMethod(application) {
    if (application?.signinMethods?.length > 0) {
      switch (application?.signinMethods[0].name) {
        case "Password":
          return "password";
        case "Verification code": {
          switch (application?.signinMethods[0].rule) {
            case "All":
              return "verificationCode"; // All
            case "Email only":
              return "verificationCodeEmail";
            case "Phone only":
              return "verificationCodePhone";
          }
          break;
        }
        case "WebAuthn":
          return "webAuthn";
        case "LDAP":
          return "ldap";
        case "Face ID":
          return "faceId";
      }
    }

    return "password";
  }

  getPlaceholder() {
    switch (this.state.loginMethod) {
      case "verificationCode":
        return i18next.t("login:Email or phone");
      case "verificationCodeEmail":
        return i18next.t("login:Email");
      case "verificationCodePhone":
        return i18next.t("login:Phone");
      case "ldap":
        return i18next.t("login:LDAP username, Email or phone");
      default:
        return i18next.t("login:Please enter email");
    }
  }

  onUpdateAccount(account) {
    this.props.onUpdateAccount(account);
  }

  onUpdateApplication(application) {
    this.props.onUpdateApplication(application);
  }

  parseOffset(offset) {
    if (
      offset === 2 ||
      offset === 4 ||
      Setting.inIframe() ||
      Setting.isMobile()
    ) {
      return "0 auto";
    }
    if (offset === 1) {
      return "0 10%";
    }
    if (offset === 3) {
      return "0 60%";
    }
  }

  populateOauthValues(values) {
    if (this.getApplicationObj()?.organization) {
      values["organization"] = this.getApplicationObj().organization;
    }

    if (this.state.loginMethod === "password") {
      values["signinMethod"] = "Password";
    } else if (this.state.loginMethod?.includes("verificationCode")) {
      values["signinMethod"] = "Verification code";
      values["password"] = "";
      // All 模式下根据输入内容区分邮箱/手机号
      const isPhone =
        this.state.loginMethod === "verificationCodePhone" ||
        (this.state.loginMethod === "verificationCode" &&
          !this.state.validEmail);
      if (isPhone) {
        values["identifierType"] = "phone";
        values["countryCode"] = this.getPhoneCountryCode();
      }
    } else if (this.state.loginMethod === "webAuthn") {
      values["signinMethod"] = "WebAuthn";
    } else if (this.state.loginMethod === "ldap") {
      values["signinMethod"] = "LDAP";
    } else if (this.state.loginMethod === "faceId") {
      values["signinMethod"] = "Face ID";
    }
    const oAuthParams = Util.getOAuthGetParameters();

    values["type"] = oAuthParams?.responseType ?? this.state.type;

    if (oAuthParams?.samlRequest) {
      values["samlRequest"] = oAuthParams.samlRequest;
      values["type"] = "saml";
      values["relayState"] = oAuthParams.relayState;
    }
  }

  sendPopupData(message, redirectUri) {
    const params = new URLSearchParams(this.props.location.search);
    if (params.get("popup") === "1") {
      window.opener.postMessage(message, redirectUri);
    }
  }

  postCodeLoginAction(resp) {
    if (resp.data === "needConsent") {
      const pendingId = resp.data2;
      Setting.goToLinkSoft(this, `/login/oauth/consent?pendingId=${pendingId}`);
      return;
    }
    const application = this.getApplicationObj();
    const ths = this;
    const oAuthParams = Util.getOAuthGetParameters();
    const code = resp.data;
    const concatChar = oAuthParams?.redirectUri?.includes("?") ? "&" : "?";
    const noRedirect = oAuthParams.noRedirect;
    const redirectUrl = `${oAuthParams.redirectUri}${concatChar}code=${code}&state=${oAuthParams.state}&auto=false`;
    // eslint-disable-next-line no-console
    console.log(
      `postCodeLoginAction: noRedirect: ${noRedirect}, redirectUrl: ${redirectUrl}`
    );
    if (resp.data === RequiredMfa) {
      this.props.onLoginSuccess(window.location.href);
      return;
    }

    if (resp.data2) {
      sessionStorage.setItem("signinUrl", window.location.href);
      Setting.goToLinkSoft(ths, `/forget/${application.name}`);
      return;
    }

    if (Setting.hasPromptPage(application)) {
      // eslint-disable-next-line no-console
      console.log("hasPromptPage enter");
      AuthBackend.getAccount().then((res) => {
        if (res.status === "ok") {
          const account = res.data;
          account.organization = res.data2;
          this.onUpdateAccount(account);

          if (Setting.isPromptAnswered(account, application)) {
            Setting.goToLink(redirectUrl);
          } else {
            Setting.goToLinkSoft(
              ths,
              `/prompt/${application.name}?redirectUri=${oAuthParams.redirectUri}&code=${code}&state=${oAuthParams.state}`
            );
          }
        } else {
          Setting.showMessage(
            "error",
            `${i18next.t("application:Failed to sign in")}: ${res.msg}`
          );
        }
      });
    } else {
      if (noRedirect === "true") {
        // eslint-disable-next-line no-console
        console.log("noRedirect enter");
        window.close();
        const newWindow = window.open(redirectUrl);
        if (newWindow) {
          setInterval(() => {
            if (!newWindow.closed) {
              newWindow.close();
            }
          }, 1000);
        }
      } else {
        // eslint-disable-next-line no-console
        console.log("redirect enter", redirectUrl);
        if (
          redirectUrl.includes("AlayaNeW.aladdin") &&
          !redirectUrl.includes("authFrom")
        ) {
          console.log(
            "redirectUrl",
            redirectUrl.includes("aladdin"),
            redirectUrl
          );
          Setting.goToLinkSoft(ths, "/aladdin/authResult");
        }
        const redirectUri = oAuthParams?.redirectUri || "";
        if (
          redirectUri.startsWith("http://localhost") ||
          redirectUri.startsWith("http://127.0.0.1")
        ) {
          fetch(redirectUrl, {method: "GET"}).catch(() => {});
          ths.setState({
            localCallbackSent: true,
            localCallbackAppName: application.displayName || application.name,
          });
          return;
        }
        Setting.goToLink(redirectUrl);
        this.sendPopupData(
          {
            type: "loginSuccess",
            data: { code: code, state: oAuthParams.state },
          },
          oAuthParams.redirectUri
        );
      }
    }
  }

  onFinish(values) {
    if (this.state.loginMethod === "webAuthn") {
      let username = this.state.username;
      if (username === null || username === "") {
        username = values["username"];
      }

      this.signInWithWebAuthn(username, values);
      return;
    }
    if (this.state.loginMethod === "faceId") {
      let username = this.state.username;
      if (username === null || username === "") {
        username = values["username"];
      }
      const application = this.getApplicationObj();
      fetch(
        `${Setting.ServerUrl}/api/faceid-signin-begin?owner=${application.organization}&name=${username}`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Accept-Language": Setting.getAcceptLanguage(),
          },
        }
      )
        .then((res) => res.json())
        .then((res) => {
          if (res.status === "error") {
            Setting.showMessage("error", res.msg);
            return;
          }
          this.setState({
            openFaceRecognitionModal: true,
            values: values,
          });
        });
      return;
    }
    if (
      this.state.loginMethod === "password" ||
      this.state.loginMethod === "ldap"
    ) {
      if (
        this.state.enableCaptchaModal === CaptchaRule.Always &&
        !this.props.isCustom &&
        !customDict.isCustomPage
      ) {
        this.setState({
          openCaptchaModal: true,
          values: values,
        });
        return;
      } else if (
        this.state.enableCaptchaModal === CaptchaRule.Dynamic &&
        !this.props.isCustom &&
        !customDict.isCustomPage
      ) {
        this.checkCaptchaStatus(values);
        return;
      }
    }
    this.login(values);
  }

  loginHandlerUpdate(res, values) {
    const oAuthParams = Util.getOAuthGetParameters();
    const responseType = values["type"];
    console.log(res, values)
    if (responseType === "login") {
      if (res.data2) {
        sessionStorage.setItem("signinUrl", window.location.href);
        Setting.goToLink(this, `/forget/${this.state.applicationName}`);
      }
      Setting.showMessage(
        "success",
        i18next.t("application:Logged in successfully")
      );
      this.props.onLoginSuccess();
    } else if (responseType === "code") {
      this.postCodeLoginAction(res);
    } else if (responseType === "token" || responseType === "id_token") {
      if (res.data2) {
        sessionStorage.setItem("signinUrl", window.location.href);
        Setting.goToLink(this, `/forget/${this.state.applicationName}`);
      }
      const amendatoryResponseType =
        responseType === "token" ? "access_token" : responseType;
      const accessToken = res.data;
      Setting.goToLink(
        `${oAuthParams.redirectUri}#${amendatoryResponseType}=${accessToken}&state=${oAuthParams.state}&token_type=bearer`
      );
    } else if (responseType === "saml") {
      if (res.data2.needUpdatePassword) {
        sessionStorage.setItem("signinUrl", window.location.href);
        Setting.goToLink(this, `/forget/${this.state.applicationName}`);
      }
      if (res.data2.method === "POST") {
        this.setState({
          samlResponse: res.data,
          redirectUrl: res.data2.redirectUrl,
          relayState: oAuthParams.relayState,
        });
      } else {
        const SAMLResponse = res.data;
        const redirectUri = res.data2.redirectUrl;
        Setting.goToLink(
          `${redirectUri}?SAMLResponse=${encodeURIComponent(
            SAMLResponse
          )}&RelayState=${oAuthParams.relayState}`
        );
      }
    }
  }

  login(values) {
    // here we are supposed to determine whether Casdoor is working as an OAuth server or CAS server
    if (this.state.type === "cas") {
      // CAS
      const casParams = Util.getCasParameters();
      values["type"] = this.state.type;
      values["password"] = Setting.Encrypt(values["password"]);
      values["username"] = Setting.Encrypt(values["username"]);
      if (
        (this.props.isCustom || customDict.isCustomPage) &&
        !this.state.loginMethod?.includes("verificationCode")
      ) {
        values["captchaType"] = this.state.CusCaptchaType;
        values["clientSecret"] = this.state.CusClientSecret;
      }
      AuthBackend.loginCas(values, casParams).then((res) => {
        const loginHandler = (res) => {
          let msg = "Logged in successfully. ";
          if (casParams.service === "") {
            // If service was not specified, Casdoor must display a message notifying the client that it has successfully initiated a single sign-on session.
            msg += "Now you can visit apps protected by Casdoor.";
          }
          Setting.showMessage("success", msg);

          if (casParams.service !== "") {
            const st = res.data;
            const newUrl = new URL(casParams.service);
            newUrl.searchParams.append("ticket", st);
            window.location.href = newUrl.toString();
          }
        };

        if (res.status === "ok") {
          if (res.data === NextMfa) {
            this.setState({
              getVerifyTotp: () => {
                return (
                  <MfaAuthVerifyForm
                    mfaProps={res.data2}
                    formValues={values}
                    authParams={casParams}
                    application={this.getApplicationObj()}
                    onFail={() => {
                      Setting.showMessage(
                        "error",
                        i18next.t("mfa:Verification failed")
                      );
                    }}
                    onSuccess={(res) => loginHandler(res)}
                  />
                );
              },
            });
          } else {
            loginHandler(res);
          }
        } else {
          if (!this.state.loginMethod?.includes("verificationCode")) {
            this.loadCaptcha(this.props.application); // 刷新验证码
          }
          Setting.showMessage(
            "error",
            `${i18next.t("application:Failed to sign in")}: ${res.msg}`
          );
        }
      });
    } else {
      // OAuth
      const oAuthParams = Util.getOAuthGetParameters();
      this.populateOauthValues(values);
      if (this.state.loginMethod?.includes("verificationCode")) {
        values["password"] = "";
      } else {
        values["password"] = Setting.Encrypt(values["password"] ?? "") || "";
      }
      values["username"] = Setting.Encrypt(values["username"] ?? "") || "";
      // 验证码登录人机校验已在发码时完成，登录请求不再附带密码登录的 captcha 字段
      if (
        (this.props.isCustom || customDict.isCustomPage) &&
        !this.state.loginMethod?.includes("verificationCode")
      ) {
        values["captchaType"] = this.state.CusCaptchaType;
        values["clientSecret"] = this.state.CusClientSecret;
      }
      AuthBackend.login(values, oAuthParams).then((res) => {
        const loginHandler = (res) => {
          const responseType = values["type"];

          if (responseType === "login") {
            if (res.data2) {
              sessionStorage.setItem("signinUrl", window.location.href);
              Setting.goToLink(this, `/forget/${this.state.applicationName}`);
            }
            Setting.showMessage(
              "success",
              i18next.t("application:Logged in successfully")
            );
            this.props.onLoginSuccess();
          } else if (responseType === "code") {
            this.postCodeLoginAction(res);
          } else if (responseType === "token" || responseType === "id_token") {
            if (res.data2) {
              sessionStorage.setItem("signinUrl", window.location.href);
              Setting.goToLink(this, `/forget/${this.state.applicationName}`);
            }
            const amendatoryResponseType =
              responseType === "token" ? "access_token" : responseType;
            const accessToken = res.data;
            Setting.goToLink(
              `${oAuthParams.redirectUri}#${amendatoryResponseType}=${accessToken}&state=${oAuthParams.state}&token_type=bearer`
            );
          } else if (responseType === "saml") {
            if (res.data2.needUpdatePassword) {
              sessionStorage.setItem("signinUrl", window.location.href);
              Setting.goToLink(this, `/forget/${this.state.applicationName}`);
            }
            if (res.data2.method === "POST") {
              this.setState({
                samlResponse: res.data,
                redirectUrl: res.data2.redirectUrl,
                relayState: oAuthParams.relayState,
              });
            } else {
              const SAMLResponse = res.data;
              const redirectUri = res.data2.redirectUrl;
              Setting.goToLink(
                `${redirectUri}?SAMLResponse=${encodeURIComponent(
                  SAMLResponse
                )}&RelayState=${oAuthParams.relayState}`
              );
            }
          }
        };

        if (res.status === "ok") {
          console.log("res", res);
          if (res.data === NextMfa) {
            this.setState({
              getVerifyTotp: () => {
                return (
                  <MfaAuthVerifyForm
                    mfaProps={res.data2}
                    formValues={values}
                    authParams={oAuthParams}
                    application={this.getApplicationObj()}
                    onFail={() => {
                      Setting.showMessage(
                        "error",
                        i18next.t("mfa:Verification failed")
                      );
                    }}
                    onSuccess={(res) => loginHandler(res)}
                  />
                );
              },
            });
          } else if (res.data === "SelectPlan") {
            // paid-user does not have active or pending subscription, go to application default pricing page to select-plan
            const pricing = res.data2;
            Setting.goToLink(
              `/select-plan/${pricing.owner}/${pricing.name}?user=${values.username}`
            );
          } else if (res.data === "BuyPlanResult") {
            // paid-user has pending subscription, go to buy-plan/result apge to notify payment result
            const sub = res.data2;
            Setting.goToLink(
              `/buy-plan/${sub.owner}/${sub.pricing}/result?subscription=${sub.name}`
            );
          } else {
            const parsedMsg = this.parseAgreementLoginMsg(res.msg);
            if (parsedMsg && parsedMsg.data.length > 0) {
              this.setState({
                updateState: true,
                logRes: res,
                formValues: values,
                agreementNotifications: parsedMsg.data,
                agreementNoticeUserId: parsedMsg.userId || "",
              });
            } else {
              console.log("loginHandlerUpdate");
              loginHandler(res);
            }
          }
        } else {
          if (!this.state.loginMethod?.includes("verificationCode")) {
            this.loadCaptcha(this.props.application); // 刷新验证码
          }
          Setting.showMessage(
            "error",
            `${i18next.t("application:Failed to sign in")}: ${res.msg}`
          );
        }
      });
    }
  }

  isProviderVisible(providerItem) {
    if (this.state.mode === "signup") {
      return Setting.isProviderVisibleForSignUp(providerItem);
    } else {
      return Setting.isProviderVisibleForSignIn(providerItem);
    }
  }


  getAgreementViewType(item) {
    const name = `${item?.agreementName || ""}`.toLowerCase();
    return (name.includes("privacy") || name.includes("隐私")) ? "privacy" : "policy";
  }

  formatPublishedDate(timeStr) {
    if (!timeStr) {
      return "-";
    }
    const date = new Date(timeStr);
    if (Number.isNaN(date.getTime())) {
      return timeStr;
    }
    const y = date.getFullYear();
    const m = `${date.getMonth() + 1}`.padStart(2, "0");
    const d = `${date.getDate()}`.padStart(2, "0");
    return `${y}.${m}.${d}`;
  }

  // 后端返回的协议名称固定为"用户服务协议"/"隐私政策"，命中时映射到本地 i18n 文案
  // （《用户协议》/《隐私政策》），随中英文切换；未命中时兜底显示原名。
  getRegistrationDocDisplayName(agreementName) {
    const name = `${agreementName || ""}`;
    if (name.includes("用户服务协议")) {
      return i18next.t("login:User Agreement");
    }
    if (name.includes("隐私政策")) {
      return i18next.t("login:Privacy Policy");
    }
    return name;
  }

  parseAgreementLoginMsg(msg) {
    if (!msg || typeof msg !== "string") {
      return null;
    }
    try {
      const parsed = JSON.parse(msg);
      if (!parsed || !Array.isArray(parsed.data)) {
        return null;
      }
      return {
        userId: parsed.userId || "",
        data: parsed.data,
      };
    } catch (e) {
      return null;
    }
  }

  renderModal() {
    // const originPath = window.origin.replace("sso", "catalog");
    const isForce = this.state.agreementNotifications.some(
      (item) => item.loginNotifyType === "NOTIFY_AND_FORCE_AGREE",
    );

    const batchBody = {
      userId: this.state.agreementNoticeUserId,
      versionIds: this.state.agreementNotifications
        .map((item) => item.versionId)
        .filter(Boolean),
    };

    const readAll = async () => {
      await AuthBackend.readAllNotifications(batchBody).then(res => console.log("res", res));
    };

    const agreeAll = async () => {
      await AuthBackend.agreeAllNotifications(batchBody).then(res => console.log("res", res));
    };

    const openAgreementPreview = async (agreementId, objectId) => {
      window.open(buildAgreementPreviewUrl(agreementId, objectId), "_blank",);
      return
      // if (!agreementId || !objectId) {
      //   Setting.showMessage("error", "协议预览文件不存在");
      //   return;
      // }
      // const previewUrl = buildAgreementPreviewUrl(agreementId, objectId);
      // const res = await fetch(previewUrl, {
      //   method: "GET",
      //   credentials: "include",
      // });
      // if (!res.ok) {
      //   Setting.showMessage("error", "Failed to preview agreement");
      //   return;
      // }
      // const blob = await res.blob();
      // const blobUrl = URL.createObjectURL(blob);
      // window.open(blobUrl, "_blank", "noopener,noreferrer");
      // setTimeout(() => URL.revokeObjectURL(blobUrl), 60 * 1000);
    };

    return (
      <Modal
        open={true}
        closable={!isForce}
        maskClosable={false}
        centered
        footer={false}
        className="custom-modal"
        onCancel={async () => {
          await readAll();
          this.loginHandlerUpdate(this.state.logRes, this.state.formValues);
        }}
      >
        <div className="notice-header">
          <div className="notice-title">
            {i18next.t("login:Agreement Update Notification")}
          </div>
        </div>
        <div className="notice-intro">
          {i18next.t("login:Agreement Update Intro")}
        </div>
        {this.state.agreementNotifications.map((item) => (
          <div className="notice-item" key={item.versionId || item.agreementId}>
            <a
              className="agreement-Paragraph-link"
              href="#"
              onClick={(e) => {
                e.preventDefault();
                const lang = Setting.getAcceptLanguage().split(";")[0];
                const objectId = lang === "en"
                  ? (item.englishFile || item.chineseFile)
                  : (item.chineseFile || item.englishFile);
                openAgreementPreview(item.agreementId, objectId);
              }}
            >
              {item.agreementName || "-"}
            </a>
            <span>
              {i18next.t("login:Protocol Update Line", {
                date: this.formatPublishedDate(item.agreementPublishedTime),
                desc: item.notifyBody || "",
              })}
            </span>
          </div>
        ))}
        <div className="notice-content">
          {i18next.t("login:Agreement Update Footer")}
        </div>
        {isForce ? (
          <>
            <Button
              className="notice-button"
              type="primary"
              block
              style={{ whiteSpace: "normal" }}
              onClick={async () => {
                await agreeAll();
                this.loginHandlerUpdate(this.state.logRes, this.state.formValues);
              }}
            >
              {i18next.t("login:Have Read and Rgreed")}
            </Button>
            {/* <div className="notice-link"> */}
            <Button
              className="notice-button"
              block
              onClick={() => {
                this.setState({
                  updateState: false,
                  formValues: {},
                  logRes: "",
                });
                // window.location.href = `${originPath}/doLogout`;
              }}
            >
              {i18next.t("login:Disagree and exit")}
            </Button>
            {/* </div> */}
          </>
        ) : (
          <Button
            className="notice-button"
            type="primary"
            block
            style={{ whiteSpace: "normal" }}
            onClick={async () => {
              await readAll();
              this.loginHandlerUpdate(this.state.logRes, this.state.formValues);
            }}
          >
            {i18next.t("login:Have Read and Continue")}
          </Button>
        )}
      </Modal>
    );
  }

  renderOtherFormProvider(application) {
    if (Setting.inIframe()) {
      return null;
    }

    for (const providerConf of application.providers) {
      if (
        providerConf.provider?.type === "Google" &&
        providerConf.rule === "OneTap" &&
        this.props.preview !== "auto"
      ) {
        return (
          <GoogleOneTapLoginVirtualButton
            application={application}
            providerConf={providerConf}
          />
        );
      }
    }

    return null;
  }

  renderFormTitle() {
    return (
      <div style={{ textAlign: "left" }}>
        <div
          className={`alaya-login-title ${
            i18next.language === "zh"
              ? "alaya-login-title-zh"
              : "alaya-login-title-en"
          }`}
        >
          {i18next.t("login:Welcome to login")}
        </div>
        <div
          className={`alaya-login-title-dec ${
            i18next.language === "zh"
              ? "alaya-login-title-dec-zh"
              : "alaya-login-title-dec-en"
          }`}
        >
          {i18next.t("login:DataCanvas Alaya NeW AIDC OS")}
        </div>
      </div>
    );
  }

  renderCaptchaInput(application) {
    return (
      <>
        <div>
          {/* {customDict.passWordLabel && <div style={{textAlign: "left", fontSize: "16px", height: "32px", lineHeight: "32px"}}>{i18next.t("forget:Verification code")}</div>} */}
          <Col span={24}>
            <div className="login-code">
              <Form.Item
                name="captchaToken"
                rules={[
                  {
                    required: true,
                    message: i18next.t(
                      "login:Please input your verification code!"
                    ),
                  },
                ]}
              >
                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "row",
                    flexWrap: "nowrap",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Input
                    className="login-code-input"
                    placeholder={i18next.t(
                      "login:Please input your verification code"
                    )}
                    style={{ borderRadius: "2px" }}
                  />
                  <div
                    className="login-code-image"
                    style={{
                      backgroundImage: `url('data:image/png;base64,${this.state.CusCaptchaImg}`,
                      backgroundSize: "contain",
                      height: "38px",
                      border: "1px solid rgb(204, 204, 204)",
                      borderRadius: "2px",
                      cursor: "pointer",
                      backgroundPosition: "50% 50%",
                      backgroundRepeat: "no-repeat",
                    }}
                    onClick={() => this.loadCaptcha(application)}
                  />
                </div>
              </Form.Item>
            </div>
          </Col>
        </div>
      </>
    );
  }

  renderCustomFormItem() {
    const application = this.getApplicationObj();
    const customCss = application.signinItems.filter(
      (item) => item?.name === "custom"
    )[0]?.customCss;
    const parser = new DOMParser();
    const doc = parser.parseFromString(customCss, "text/html");
    const hrefs = Array.from(doc.querySelectorAll("a")).map((a) =>
      a.getAttribute("href")
    );
    const registerHref = hrefs.filter((item) => item.includes("register"))[0];
    // const agreementHref = hrefs.filter(item => item.includes(`${i18next.t("login:agreement.html")}`))[0];
    // const privacyHref = hrefs.filter(item => item.includes(`${i18next.t("login:privacy.html")}`))[0];
    const originPath = window.location.host.includes("zetyun.cn")
      ? window.origin.replace("sso", "catalog")
      : window.origin.replace("sso", "www"); // 兼容马鞍山域名
    const codeLoginMethod = this.getCodeLoginMethod(application);
    const signinTabItems = [];
    if (this.isPasswordLoginVisible(application)) {
      signinTabItems.push({
        key: "password",
        label: i18next.t("login:Password login"),
      });
    }
    if (codeLoginMethod) {
      signinTabItems.push({
        key: codeLoginMethod,
        label: i18next.t("login:Verification code login"),
      });
    }
    return (
      <>
        {/* Logo */}
        <div className="login-logo-box">
          {
            // Setting.renderHelmet(application)
            Setting.renderHelmet({
              organizationObj: {
                displayName: i18next.t("login:Login - Alaya NeW AIDC OS"),
                favicon: require("../assets/favicon.ico"),
              },
            })
          }
          {this.renderFormTitle()}
        </div>
        {signinTabItems.length > 1 ? (
          <Tabs
            className="custom-signin-methods"
            activeKey={this.state.loginMethod}
            items={signinTabItems}
            onChange={(loginMethod) => {
              // 切换登录方式时清空账号、密码、验证码，避免残留信息带到另一种登录方式
              this.form.current?.setFieldsValue({
                username: "",
                password: "",
                code: "",
              });
              this.form.current?.setFields([
                { name: "username", errors: [] },
                { name: "password", errors: [] },
                { name: "code", errors: [] },
              ]);
              this.setState({
                loginMethod,
                username: "",
                validEmail: false,
                validEmailOrPhone: false,
              });
            }}
          />
        ) : null}
        {/* Username */}
        <div>
          {/* {customDict.emailLabel && <div style={{textAlign: "left", fontSize: "16px", height: "32px", lineHeight: "32px"}}>{i18next.t("forget:Account")}</div>} */}
          <Form.Item
            name="username"
            className="login-username"
            rules={[
              {
                required: true,
                message: () => {
                  switch (this.state.loginMethod) {
                    case "verificationCodeEmail":
                      return i18next.t("login:Please input your Email!");
                    case "verificationCodePhone":
                      return i18next.t("login:Please input your Phone!");
                    case "ldap":
                      return i18next.t(
                        "login:Please input your LDAP username!"
                      );
                    default:
                      return i18next.t("login:Please enter email!"); // return i18next.t("login:Please input your Email or Phone!");
                  }
                },
              },
              {
                validator: (_, value) => {
                  if (value === "") {
                    return Promise.resolve();
                  }

                  if (this.state.loginMethod === "verificationCode") {
                    if (
                      !Setting.isValidEmail(value) &&
                      !Setting.isValidPhone(value)
                    ) {
                      this.setState({ validEmailOrPhone: false });
                      return Promise.reject(
                        i18next.t(
                          "login:The input is not valid Email or phone number!"
                        )
                      );
                    }

                    if (Setting.isValidEmail(value)) {
                      this.setState({ validEmail: true });
                    } else {
                      this.setState({ validEmail: false });
                    }
                  } else if (
                    this.state.loginMethod === "verificationCodeEmail"
                  ) {
                    if (!Setting.isValidEmail(value)) {
                      this.setState({ validEmail: false });
                      this.setState({ validEmailOrPhone: false });
                      return Promise.reject(
                        i18next.t("login:The input is not valid Email!")
                      );
                    } else {
                      this.setState({ validEmail: true });
                    }
                  } else if (
                    this.state.loginMethod === "verificationCodePhone"
                  ) {
                    if (!Setting.isValidPhone(value)) {
                      this.setState({ validEmailOrPhone: false });
                      return Promise.reject(
                        i18next.t("login:The input is not valid phone number!")
                      );
                    }
                  }

                  this.setState({ validEmailOrPhone: true });
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input
              id="input"
              className="login-username-input"
              // prefix={<UserOutlined className="site-form-item-icon" />}
              style={{ borderRadius: "2px" }}
              placeholder={this.getPlaceholder()}
              onChange={(e) => {
                this.setState({
                  username: e.target.value,
                });
              }}
            />
          </Form.Item>
        </div>
        {/* 密码或手机验证码 */}
        {this.renderPasswordOrCodeInput({ label: null })}
        {/* Captcha */}
        {this.state.loginMethod === "password" &&
          application?.providers?.length > 0 &&
          this.renderCaptchaInput(application)}

        <div id="alayanew" className="alayaNeW-agreement">
          <Paragraph className="agreement-Paragraph">
            {i18next.t("login:Read and agreed")}
            {this.state.registrationDocuments.length > 0 ? (
              this.state.registrationDocuments.map((doc, index) => (
                <React.Fragment key={doc.agreementId}>
                  {index > 0 && (
                    <span
                      style={{ color: "rgba(0,0,0,0.88)", marginLeft: "6px" }}
                    >
                      {i18next.t("login:And")}
                    </span>
                  )}
                  <a
                    className="agreement-Paragraph-link"
                    href={buildAgreementPreviewUrl(
                      doc.agreementId,
                      doc.objectMeta?.id
                    )}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {this.getRegistrationDocDisplayName(doc.agreementName)}
                  </a>
                </React.Fragment>
              ))
            ) : (
              <>
                <a
                  className="agreement-Paragraph-link"
                  href={`${window.origin}/view/policy/${
                    Setting.getAcceptLanguage().split(";")[0]
                  }`}
                  target="_agreement"
                >
                  {i18next.t("login:User Agreement")}
                </a>
                <span style={{ color: "rgba(0,0,0,0.88)", marginLeft: "6px" }}>
                  {i18next.t("login:And")}
                </span>
                <a
                  className="agreement-Paragraph-link"
                  href={`${window.origin}/view/privacy/${
                    Setting.getAcceptLanguage().split(";")[0]
                  }`}
                  target="_privacy"
                >
                  {i18next.t("login:Privacy Policy")}
                </a>
              </>
            )}
          </Paragraph>
        </div>
        <Form.Item className="login-button-box">
          <Button
            type="primary"
            htmlType="submit"
            className="login-button"
            style={{ width: "100%", borderRadius: "2px" }}
            onClick={() => {
              this.trackSend.trackSend({
                trackEventName: "sso_login_click",
              });
            }}
          >
            {this.state.loginMethod === "webAuthn"
              ? i18next.t("login:Sign in with WebAuthn")
              : this.state.loginMethod === "faceId"
              ? i18next.t("login:Sign in with Face ID")
              : // signinItem.label ? signinItem.label : i18next.t("login:Sign In")
                i18next.t("login:Sign In")}
          </Button>
        </Form.Item>
        {application.providers.filter((providerItem) =>
          this.isProviderVisible(providerItem)
        ).length > 0 && (
          <>
            <Divider plain>{i18next.t("user:3rd-party logins")}</Divider>
            <Form.Item>
              {application.providers
                .map((providerItem) => {
                  console.log(
                    "providerItem1111~~~~~",
                    providerItem,
                    this.isProviderVisible(providerItem),
                    this.state.mode
                  );
                  return providerItem;
                })
                .filter((providerItem) => this.isProviderVisible(providerItem))
                .map((providerItem, id) => {
                  return (
                    <span
                      key={id}
                      onClick={() => {
                        if (providerItem.name.toLowerCase().includes("csdn")) {
                          this.trackSend.trackSend({
                            trackEventName: "sso_csdn_click",
                          });
                        } else if (providerItem.name.toLowerCase().includes("wechat")) {
                          this.trackSend.trackSend({
                            trackEventName: "sso_wechat_click",
                          });
                        }
                      }}
                    >
                      {ProviderButton.renderProviderLogo(
                        providerItem.provider,
                        application,
                        providerItem.provider?.type === "Custom" ? 42 : 38,
                        "0 12px",
                        "small",
                        this.props.location,
                        (provider) =>
                          this.setState({
                            wechatQRMode: true,
                            wechatQRProvider: provider,
                          })
                      )}
                    </span>
                  );
                })}
            </Form.Item>
          </>
        )}

        <div className="formFooterLink">
          {Setting.renderForgetLink(
            application,
            i18next.t("forget:Forget Password")
          )}
          <a
            href={getURLWithUtmParams(
              registerHref || `${originPath}/backend/register`
            )}
            style={{ float: "right" }}
            onClick={() => {
              this.trackSend.trackSend({
                trackEventName: "sso_register_click",
              });
            }}
          >
            {i18next.t("login:User registration")}
          </a>
        </div>
      </>
    );
  }

  renderFormItem(application, signinItem) {
    if (
      !signinItem.visible &&
      (signinItem.name !== "Forgot password?" ||
        signinItem.name !== "Agreement")
    ) {
      return null;
    }

    if (signinItem.name === "Logo") {
      return (
        <div className="login-logo-box">
          <div
            dangerouslySetInnerHTML={{
              __html:
                "<style>" +
                signinItem.customCss
                  ?.replaceAll("<style>", "")
                  .replaceAll("</style>", "") +
                "</style>",
            }}
          />
          {
            // Setting.renderHelmet(application)
            Setting.renderHelmet({
              organizationObj: {
                displayName: i18next.t("login:Login - Alaya NeW AIDC OS"),
                favicon: require("../assets/favicon.ico"),
              },
            })
          }
          {this.renderFormTitle() ?? Setting.renderLogo(application)}
        </div>
      );
    } else if (signinItem.name === "Back button") {
      return (
        <div className="back-button">
          <div
            dangerouslySetInnerHTML={{
              __html:
                "<style>" +
                signinItem.customCss
                  ?.replaceAll("<style>", "")
                  .replaceAll("</style>", "") +
                "</style>",
            }}
          />
          {this.renderBackButton()}
        </div>
      );
    } else if (signinItem.name === "Languages") {
      const languages = application.organizationObj.languages;
      if (languages.length <= 1) {
        const language = languages.length === 1 ? languages[0] : "en";
        if (Setting.getLanguage() !== language) {
          Setting.setLanguage(language);
        }
        return null;
      }

      return customDict.isCustomPage ? (
        <></>
      ) : (
        <div className="login-languages">
          <div
            dangerouslySetInnerHTML={{
              __html:
                "<style>" +
                signinItem.customCss
                  ?.replaceAll("<style>", "")
                  .replaceAll("</style>", "") +
                "</style>",
            }}
          />
          <LanguageSelect languages={application.organizationObj.languages} />
        </div>
      );
    } else if (signinItem.name === "Signin methods") {
      return (
        <div>
          <div
            dangerouslySetInnerHTML={{
              __html:
                "<style>" +
                signinItem.customCss
                  ?.replaceAll("<style>", "")
                  .replaceAll("</style>", "") +
                "</style>",
            }}
          />
          {this.renderMethodChoiceBox()}
        </div>
      );
    } else if (signinItem.name === "Username") {
      return (
        <div>
          <div
            dangerouslySetInnerHTML={{
              __html:
                "<style>" +
                signinItem.customCss
                  ?.replaceAll("<style>", "")
                  .replaceAll("</style>", "") +
                "</style>",
            }}
          />
          {/* {customDict.emailLabel && <div style={{textAlign: "left", fontSize: "16px", height: "32px", lineHeight: "32px"}}>{i18next.t("forget:Account")}</div>} */}
          <Form.Item
            name="username"
            className="login-username"
            label={signinItem.label ? signinItem.label : null}
            rules={[
              {
                required: true,
                message: () => {
                  switch (this.state.loginMethod) {
                    case "verificationCodeEmail":
                      return i18next.t("login:Please input your Email!");
                    case "verificationCodePhone":
                      return i18next.t("login:Please input your Phone!");
                    case "ldap":
                      return i18next.t(
                        "login:Please input your LDAP username!"
                      );
                    default:
                      return i18next.t("login:Please enter email!"); // return i18next.t("login:Please input your Email or Phone!");
                  }
                },
              },
              {
                validator: (_, value) => {
                  if (value === "") {
                    return Promise.resolve();
                  }

                  if (this.state.loginMethod === "verificationCode") {
                    if (
                      !Setting.isValidEmail(value) &&
                      !Setting.isValidPhone(value)
                    ) {
                      this.setState({ validEmailOrPhone: false });
                      return Promise.reject(
                        i18next.t(
                          "login:The input is not valid Email or phone number!"
                        )
                      );
                    }

                    if (Setting.isValidEmail(value)) {
                      this.setState({ validEmail: true });
                    } else {
                      this.setState({ validEmail: false });
                    }
                  } else if (
                    this.state.loginMethod === "verificationCodeEmail"
                  ) {
                    if (!Setting.isValidEmail(value)) {
                      this.setState({ validEmail: false });
                      this.setState({ validEmailOrPhone: false });
                      return Promise.reject(
                        i18next.t("login:The input is not valid Email!")
                      );
                    } else {
                      this.setState({ validEmail: true });
                    }
                  } else if (
                    this.state.loginMethod === "verificationCodePhone"
                  ) {
                    if (!Setting.isValidPhone(value)) {
                      this.setState({ validEmailOrPhone: false });
                      return Promise.reject(
                        i18next.t("login:The input is not valid phone number!")
                      );
                    }
                  }

                  this.setState({ validEmailOrPhone: true });
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input
              id="input"
              className="login-username-input"
              style={{ borderRadius: "2px" }}
              // prefix={<UserOutlined className="site-form-item-icon" />}
              placeholder={this.getPlaceholder()}
              onChange={(e) => {
                this.setState({
                  username: e.target.value,
                });
              }}
            />
          </Form.Item>
        </div>
      );
    } else if (signinItem.name === "Password") {
      return (
        <div>
          <div
            dangerouslySetInnerHTML={{
              __html:
                "<style>" +
                signinItem.customCss
                  ?.replaceAll("<style>", "")
                  .replaceAll("</style>", "") +
                "</style>",
            }}
          />
          {this.renderPasswordOrCodeInput(signinItem)}
          {customDict.isCustomPage &&
            this.state.loginMethod === "password" &&
            this.renderCaptchaInput(application)}
        </div>
      );
    } else if (signinItem.name === "Forgot password?") {
      return customDict.isCustomPage ? (
        <></>
      ) : (
        <div>
          <div
            dangerouslySetInnerHTML={{
              __html:
                "<style>" +
                signinItem.customCss
                  ?.replaceAll("<style>", "")
                  .replaceAll("</style>", "") +
                "</style>",
            }}
          />
          <div className="login-forget-password">
            <Form.Item name="autoSignin" valuePropName="checked" noStyle>
              <Checkbox style={{ float: "left" }}>
                {i18next.t("login:Auto sign in")}
              </Checkbox>
            </Form.Item>
            {signinItem.visible
              ? Setting.renderForgetLink(
                  application,
                  signinItem.label
                    ? signinItem.label
                    : i18next.t("login:Forgot password?")
                )
              : null}
          </div>
        </div>
      );
    } else if (signinItem.name === "Agreement") {
      return customDict.isCustomPage ? (
        // <div id="alayanew" style={{fontSize: 16}}>
        //   <div style={{marginBottom: "24px", display: "flex"}}>{i18next.t("login:Read and agreed")}
        //     <a href="https://bsm.unite.zetyun.cn/agreement.html" target="_agreement" >{i18next.t("login:User Agreement")}</a>
        //     <a href="#" target="_agreement" >{i18next.t("login:And")}</a>
        //     <a href="https://bsm.unite.zetyun.cn/privacy.html" target="_privacy" >{i18next.t("login:User Agreement")}</a>
        //   </div>
        // </div>
        <></>
      ) : AgreementModal.isAgreementRequired(application) ? (
        AgreementModal.renderAgreementFormItem(application, true, {}, this)
      ) : null;
    } else if (signinItem.name === "Login button") {
      return (
        <Form.Item className="login-button-box">
          <div
            dangerouslySetInnerHTML={{
              __html:
                "<style>" +
                signinItem.customCss
                  ?.replaceAll("<style>", "")
                  .replaceAll("</style>", "") +
                "</style>",
            }}
          />
          <Button
            type="primary"
            htmlType="submit"
            className="login-button"
            style={{ borderRadius: "2px" }}
          >
            {this.state.loginMethod === "webAuthn"
              ? i18next.t("login:Sign in with WebAuthn")
              : this.state.loginMethod === "faceId"
              ? i18next.t("login:Sign in with Face ID")
              : signinItem.label
              ? signinItem.label
              : i18next.t("login:Sign In")}
          </Button>
          {this.state.loginMethod === "faceId" ? (
            <Suspense fallback={null}>
              <FaceRecognitionModal
                visible={this.state.openFaceRecognitionModal}
                onOk={(faceId) => {
                  const values = this.state.values;
                  values["faceId"] = faceId;

                  this.login(values);
                  this.setState({ openFaceRecognitionModal: false });
                }}
                onCancel={() =>
                  this.setState({ openFaceRecognitionModal: false })
                }
              />
            </Suspense>
          ) : (
            <></>
          )}
          {this.renderCaptchaModal(application)}
        </Form.Item>
      );
    } else if (signinItem.name === "Providers") {
      const showForm =
        Setting.isPasswordEnabled(application) ||
        Setting.isCodeSigninEnabled(application) ||
        Setting.isWebAuthnEnabled(application) ||
        Setting.isLdapEnabled(application);
      if (signinItem.rule === "None" || signinItem.rule === "") {
        signinItem.rule = showForm ? "small" : "big";
      }
      return customDict.isCustomPage ? (
        <></>
      ) : (
        <div>
          <div
            dangerouslySetInnerHTML={{
              __html:
                "<style>" +
                signinItem.customCss
                  ?.replaceAll("<style>", "")
                  .replaceAll("</style>", "") +
                "</style>",
            }}
          />
          <Form.Item>
            {application.providers
              .filter((providerItem) => this.isProviderVisible(providerItem))
              .map((providerItem, id) => {
                return (
                  <span
                    key={id}
                    onClick={(e) => {
                      if (!this.form.current.getFieldValue("agreement")) {
                        e.preventDefault();
                        message.error(
                          i18next.t("signup:Please accept the agreement!")
                        );
                      }
                    }}
                  >
                    {ProviderButton.renderProviderLogo(
                      providerItem.provider,
                      application,
                      null,
                      null,
                      signinItem.rule,
                      this.props.location,
                      (provider) =>
                        this.setState({
                          wechatQRMode: true,
                          wechatQRProvider: provider,
                        })
                    )}
                  </span>
                );
              })}
            {this.renderOtherFormProvider(application)}
          </Form.Item>
        </div>
      );
    } else if (signinItem.name.startsWith("Text ") || signinItem?.isCustom) {
      return customDict.isCustomPage ? (
        <></>
      ) : (
        <div dangerouslySetInnerHTML={{ __html: signinItem.customCss }} />
      );
    } else if (signinItem.name === "Signup link") {
      return (
        <div style={{ width: "100%" }} className="login-signup-link">
          {/* <div dangerouslySetInnerHTML={{__html: ("<style>" + signinItem.customCss?.replaceAll("<style>", "").replaceAll("</style>", "") + "</style>")}} /> */}
          {this.renderFooter(application, signinItem)}
        </div>
      );
    }
  }

  // 渲染微信二维码组件
  renderWechatQRCode() {
    return (
      <div style={{ textAlign: "center" }}>
        <div style={{ textAlign: "left" }}>
          <Button
          size="small"
           className="forget-button" 
           icon={<ArrowLeftOutlined />}  
           onClick={() => this.setState({ wechatQRMode: false, wechatQRProvider: null })}
           style={{ border: 'none', padding: 0, boxShadow: 'none' }}
           >
            <span>{i18next.t("login:Back to login")}</span>
          </Button>
        </div>
        <Spin spinning={this.state.wechatQRLoading}>
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400 }}>
            <div id="wechat-qr-container" style={{ height: 'unset'}} />
          </div>
          {/* <div
            id="wechat-qr-container"
            style={{
              display: "flex",
              justifyContent: "center",
              minHeight: 300,
            }}
          /> */}
        </Spin>
      </div>
    );
  }

  renderForm(application) {
    if (this.state.msg !== null) {
      return Util.renderMessage(this.state.msg);
    }

    if (this.state.mode === "signup" && !application.enableSignUp) {
      return (
        <Result
          status="error"
          title={i18next.t("application:Sign Up Error")}
          subTitle={i18next.t(
            "application:The application does not allow to sign up new account"
          )}
          extra={[
            <Button
              type="primary"
              key="signin"
              onClick={() =>
                Setting.redirectToLoginPage(application, this.props.history)
              }
            >
              {i18next.t("login:Sign In")}
            </Button>,
          ]}
        ></Result>
      );
    }

    const showForm =
      Setting.isPasswordEnabled(application) ||
      Setting.isCodeSigninEnabled(application) ||
      Setting.isWebAuthnEnabled(application) ||
      Setting.isLdapEnabled(application) ||
      Setting.isFaceIdEnabled(application);
    if (showForm) {
      // let loginWidth = 480;
      // if (Setting.getLanguage() === "fr") {
      //   loginWidth += 20;
      // } else if (Setting.getLanguage() === "es") {
      //   loginWidth += 40;
      // } else if (Setting.getLanguage() === "ru") {
      //   loginWidth += 10;
      // }

      return (
        <Form
          className="login-custom-form"
          name="normal_login"
          initialValues={{
            organization: application.organization,
            application: application.name,
            autoSignin: true,
            username: Conf.ShowGithubCorner ? "admin" : "",
            password: Conf.ShowGithubCorner ? "123" : "",
          }}
          onFinish={(values) => {
            this.onFinish(values);
          }}
          // style={{width: `${loginWidth}px`}}
          size="large"
          ref={this.form}
        >
          <Form.Item
            hidden={true}
            name="application"
            rules={[
              {
                required: true,
                message: i18next.t(
                  "application:Please input your application!"
                ),
              },
            ]}
          ></Form.Item>
          <Form.Item
            hidden={true}
            name="organization"
            rules={[
              {
                required: true,
                message: i18next.t(
                  "application:Please input your organization!"
                ),
              },
            ]}
          ></Form.Item>
          {this.state.wechatQRMode
            ? this.renderWechatQRCode()
            : this.props.isCustom
            ? this.renderCustomFormItem()
            : application.signinItems?.map((signinItem) =>
                this.renderFormItem(application, signinItem)
              )}
        </Form>
      );
    } else {
      return (
        <div style={{ marginTop: "20px" }}>
          {this.state.wechatQRMode ? (
            this.renderWechatQRCode()
          ) : (
            <React.Fragment>
              <div style={{ fontSize: 16, textAlign: "left" }}>
                {i18next.t("login:To access")}&nbsp;
                <a
                  target="_blank"
                  rel="noreferrer"
                  href={application.homepageUrl}
                >
                  <span style={{ fontWeight: "bold" }}>
                    {application.displayName}
                  </span>
                </a>
                :
              </div>
              <br />
              {application?.signinItems.map((signinItem) =>
                signinItem.name === "Providers" ||
                signinItem.name === "Signup link"
                  ? this.renderFormItem(application, signinItem)
                  : null
              )}
            </React.Fragment>
          )}
        </div>
      );
    }
  }

  getCaptchaProviderItems(application) {
    const providers = application?.providers;

    if (providers === undefined || providers === null) {
      return null;
    }

    return providers.filter((providerItem) => {
      if (
        providerItem.provider === undefined ||
        providerItem.provider === null
      ) {
        return false;
      }

      return providerItem.provider.category === "Captcha";
    });
  }

  renderCaptchaModal(application) {
    if (this.state.enableCaptchaModal === CaptchaRule.Never) {
      return null;
    }
    const captchaProviderItems = this.getCaptchaProviderItems(application);
    const alwaysProviderItems = captchaProviderItems.filter(
      (providerItem) => providerItem.rule === "Always"
    );
    const dynamicProviderItems = captchaProviderItems.filter(
      (providerItem) => providerItem.rule === "Dynamic"
    );
    const provider =
      alwaysProviderItems.length > 0
        ? alwaysProviderItems[0].provider
        : dynamicProviderItems[0].provider;

    return (
      <CaptchaModal
        owner={provider.owner}
        name={provider.name}
        visible={this.state.openCaptchaModal}
        onOk={(captchaType, captchaToken, clientSecret) => {
          const values = this.state.values;
          values["captchaType"] = captchaType;
          values["captchaToken"] = captchaToken;
          values["clientSecret"] = clientSecret;

          this.login(values);
          this.setState({ openCaptchaModal: false });
        }}
        onCancel={() => this.setState({ openCaptchaModal: false })}
        isCurrentProvider={true}
      />
    );
  }

  renderFooter(application, signinItem) {
    return (
      <div style={{ width: "100%" }}>
        {!application.enableSignUp ? null : signinItem.label ? (
          Setting.renderSignupLink(application, signinItem.label)
        ) : customDict.isCustomPage ? (
          <div className="formFooterLink">
            {Setting.renderForgetLink(
              application,
              i18next.t("forget:Forget Password")
            )}
            {/* {Setting.renderSignupLink(application, i18next.t("login:sign up now"))} */}
          </div>
        ) : (
          <React.Fragment>
            {i18next.t("login:No account?")}
            {Setting.renderSignupLink(
              application,
              i18next.t("login:sign up now")
            )}
          </React.Fragment>
        )}
      </div>
    );
  }

  sendSilentSigninData(data) {
    if (Setting.inIframe()) {
      const message = { tag: "Casdoor", type: "SilentSignin", data: data };
      window.parent.postMessage(message, "*");
    }
  }

  renderSignedInBox() {
    if (this.props.account === undefined || this.props.account === null) {
      this.sendSilentSigninData("user-not-logged-in");
      return null;
    }

    const application = this.getApplicationObj();
    if (this.props.account.owner !== application?.organization) {
      return null;
    }

    return (
      <div>
        <div style={{ fontSize: 16, textAlign: "left" }}>
          {i18next.t("login:Continue with")}&nbsp;:
        </div>
        <br />
        <SelfLoginButton
          account={this.props.account}
          onClick={() => {
            const values = {};
            values["application"] = application.name;
            this.login(values);
          }}
        />
        <br />
        <br />
        <div style={{ fontSize: 16, textAlign: "left" }}>
          {i18next.t("login:Or sign in with another account")}&nbsp;:
        </div>
      </div>
    );
  }

  signInWithWebAuthn(username, values) {
    const oAuthParams = Util.getOAuthGetParameters();
    this.populateOauthValues(values);
    const application = this.getApplicationObj();
    return fetch(
      `${Setting.ServerUrl}/api/webauthn/signin/begin?owner=${application.organization}&name=${username}`,
      {
        method: "GET",
        credentials: "include",
      }
    )
      .then((res) => res.json())
      .then((credentialRequestOptions) => {
        if ("status" in credentialRequestOptions) {
          Setting.showMessage("error", credentialRequestOptions.msg);
          throw credentialRequestOptions.status.msg;
        }

        credentialRequestOptions.publicKey.challenge =
          UserWebauthnBackend.webAuthnBufferDecode(
            credentialRequestOptions.publicKey.challenge
          );
        credentialRequestOptions.publicKey.allowCredentials.forEach(function (
          listItem
        ) {
          listItem.id = UserWebauthnBackend.webAuthnBufferDecode(listItem.id);
        });

        return navigator.credentials.get({
          publicKey: credentialRequestOptions.publicKey,
        });
      })
      .then((assertion) => {
        const authData = assertion.response.authenticatorData;
        const clientDataJSON = assertion.response.clientDataJSON;
        const rawId = assertion.rawId;
        const sig = assertion.response.signature;
        const userHandle = assertion.response.userHandle;
        let finishUrl = `${Setting.ServerUrl}/api/webauthn/signin/finish?responseType=${values["type"]}`;
        if (values["type"] === "code") {
          finishUrl = `${Setting.ServerUrl}/api/webauthn/signin/finish?responseType=${values["type"]}&clientId=${oAuthParams.clientId}&scope=${oAuthParams.scope}&redirectUri=${oAuthParams.redirectUri}&nonce=${oAuthParams.nonce}&state=${oAuthParams.state}&codeChallenge=${oAuthParams.codeChallenge}&challengeMethod=${oAuthParams.challengeMethod}`;
        }
        return fetch(finishUrl, {
          method: "POST",
          credentials: "include",
          body: JSON.stringify({
            id: assertion.id,
            rawId: UserWebauthnBackend.webAuthnBufferEncode(rawId),
            type: assertion.type,
            response: {
              authenticatorData:
                UserWebauthnBackend.webAuthnBufferEncode(authData),
              clientDataJSON:
                UserWebauthnBackend.webAuthnBufferEncode(clientDataJSON),
              signature: UserWebauthnBackend.webAuthnBufferEncode(sig),
              userHandle: UserWebauthnBackend.webAuthnBufferEncode(userHandle),
            },
          }),
        })
          .then((res) => res.json())
          .then((res) => {
            if (res.status === "ok") {
              const responseType = values["type"];
              if (responseType === "code") {
                this.postCodeLoginAction(res);
              } else if (
                responseType === "token" ||
                responseType === "id_token"
              ) {
                const accessToken = res.data;
                Setting.goToLink(
                  `${oAuthParams.redirectUri}#${responseType}=${accessToken}?state=${oAuthParams.state}&token_type=bearer`
                );
              } else {
                Setting.showMessage(
                  "success",
                  i18next.t(
                    "login:Successfully logged in with WebAuthn credentials"
                  )
                );
                Setting.goToLink("/");
              }
            } else {
              Setting.showMessage("error", res.msg);
            }
          })
          .catch((error) => {
            Setting.showMessage(
              "error",
              `${i18next.t("general:Failed to connect to server")}${error}`
            );
          });
      });
  }

  renderPasswordOrCodeInput(signinItem) {
    const application = this.getApplicationObj();
    if (
      this.state.loginMethod === "password" ||
      this.state.loginMethod === "ldap"
    ) {
      return (
        <>
          {/* {customDict.passWordLabel && <div style={{textAlign: "left", fontSize: "16px", height: "32px", lineHeight: "32px"}}>{i18next.t("general:Password")}</div>} */}
          <Col span={24}>
            <div>
              <Form.Item
                name="password"
                className="login-password"
                label={signinItem.label ? signinItem.label : null}
                rules={[
                  {
                    required: true,
                    message: i18next.t("login:Please enter password!"),
                  },
                ]}
              >
                <Input.Password
                  className="login-password-input"
                  style={{ borderRadius: "2px" }}
                  // prefix={<LockOutlined className="site-form-item-icon" />}
                  type="password"
                  placeholder={i18next.t("login:Please enter password")}
                  disabled={
                    this.state.loginMethod === "password"
                      ? !Setting.isPasswordEnabled(application)
                      : !Setting.isLdapEnabled(application)
                  }
                />
              </Form.Item>
            </div>
          </Col>
        </>
      );
    } else if (this.state.loginMethod?.includes("verificationCode")) {
      return (
        <Col span={24}>
          <div className="login-password">
            <Form.Item
              name="code"
              rules={[
                {
                  required: true,
                  message: i18next.t("login:Please input your code!"),
                },
              ]}
            >
              <SendCodeInput
                disabled={
                  this.state.username?.length === 0 ||
                  !this.state.validEmailOrPhone
                }
                method={"login"}
                countryCode={this.getPhoneCountryCode()}
                onButtonClickArgs={[
                  this.state.username,
                  this.state.loginMethod === "verificationCodePhone"
                    ? "phone"
                    : this.state.validEmail
                    ? "email"
                    : "phone",
                  Setting.getApplicationName(application),
                ]}
                application={application}
              />
            </Form.Item>
          </div>
        </Col>
      );
    } else {
      return null;
    }
  }

  renderMethodChoiceBox() {
    const application = this.getApplicationObj();
    const items = [];

    const generateItemKey = (name, rule) => {
      return `${name}-${rule}`;
    };

    const itemsMap = new Map([
      [
        generateItemKey("Password", "All"),
        { label: i18next.t("general:Password"), key: "password" },
      ],
      [
        generateItemKey("Password", "Non-LDAP"),
        { label: i18next.t("general:Password"), key: "password" },
      ],
      [
        generateItemKey("Verification code", "All"),
        {
          label: i18next.t("login:Verification code"),
          key: "verificationCode",
        },
      ],
      [
        generateItemKey("Verification code", "Email only"),
        {
          label: i18next.t("login:Verification code"),
          key: "verificationCodeEmail",
        },
      ],
      [
        generateItemKey("Verification code", "Phone only"),
        {
          label: i18next.t("login:Verification code"),
          key: "verificationCodePhone",
        },
      ],
      // [generateItemKey("WebAuthn", "None"), {label: i18next.t("login:WebAuthn"), key: "webAuthn"}],
      [
        generateItemKey("LDAP", "None"),
        { label: i18next.t("login:LDAP"), key: "ldap" },
      ],
      // [generateItemKey("Face ID", "None"), {label: i18next.t("login:Face ID"), key: "faceId"}],
    ]);

    application?.signinMethods?.forEach((signinMethod) => {
      const item = itemsMap.get(
        generateItemKey(signinMethod.name, signinMethod.rule)
      );
      if (item) {
        let label =
          signinMethod.name === signinMethod.displayName
            ? item.label
            : signinMethod.displayName;

        if (
          application?.signinMethods?.length >= 4 &&
          label === "Verification code"
        ) {
          label = "Code";
        }

        items.push({ label: label, key: item.key });
      }
    });

    if (items.length > 1) {
      return (
        <div>
          <Tabs
            className="signin-methods"
            items={items}
            size={"small"}
            defaultActiveKey={this.getDefaultLoginMethod(application)}
            onChange={(key) => {
              // 切换登录方式时清空账号、密码、验证码，避免残留信息带到另一种登录方式
              this.form.current?.setFieldsValue({
                username: "",
                password: "",
                code: "",
              });
              this.form.current?.setFields([
                { name: "username", errors: [] },
                { name: "password", errors: [] },
                { name: "code", errors: [] },
              ]);
              this.setState({
                loginMethod: key,
                username: "",
                validEmail: false,
                validEmailOrPhone: false,
              });
            }}
            centered
          ></Tabs>
        </div>
      );
    }
  }

  renderLoginPanel(application) {
    const orgChoiceMode = application.orgChoiceMode;

    if (this.isOrganizationChoiceBoxVisible(orgChoiceMode)) {
      return this.renderOrganizationChoiceBox(orgChoiceMode);
    }

    if (this.state.getVerifyTotp !== undefined) {
      return this.state.getVerifyTotp();
    } else {
      return (
        <React.Fragment>
          {/* {this.renderSignedInBox()} */}
          {this.renderForm(application)}
        </React.Fragment>
      );
    }
  }

  renderOrganizationChoiceBox(orgChoiceMode) {
    const renderChoiceBox = () => {
      switch (orgChoiceMode) {
        case "None":
          return null;
        case "Select":
          return (
            <div>
              <p style={{ fontSize: "large" }}>
                {i18next.t("login:Please select an organization to sign in")}
              </p>
              <OrganizationSelect
                style={{ width: "70%" }}
                onSelect={(value) => {
                  Setting.goToLink(`/login/${value}?orgChoiceMode=None`);
                }}
              />
            </div>
          );
        case "Input":
          return (
            <div>
              <p style={{ fontSize: "large" }}>
                {i18next.t("login:Please type an organization to sign in")}
              </p>
              <Form
                name="basic"
                onFinish={(values) => {
                  Setting.goToLink(
                    `/login/${values.organizationName}?orgChoiceMode=None`
                  );
                }}
              >
                <Form.Item
                  name="organizationName"
                  rules={[
                    {
                      required: true,
                      message: i18next.t(
                        "login:Please input your organization name!"
                      ),
                    },
                  ]}
                >
                  <Input
                    style={{ width: "70%" }}
                    onPressEnter={(e) => {
                      Setting.goToLink(
                        `/login/${e.target.value}?orgChoiceMode=None`
                      );
                    }}
                  />
                </Form.Item>
                <Button type="primary" htmlType="submit">
                  {i18next.t("general:Confirm")}
                </Button>
              </Form>
            </div>
          );
        default:
          return null;
      }
    };

    return (
      <div style={{ height: 300, minWidth: 480 }}>{renderChoiceBox()}</div>
    );
  }

  isOrganizationChoiceBoxVisible(orgChoiceMode) {
    if (this.state.orgChoiceMode === "None") {
      return false;
    }

    const path = this.props.match?.path;
    if (path === "/login" || path === "/login/:owner") {
      return orgChoiceMode === "Select" || orgChoiceMode === "Input";
    }

    return false;
  }

  renderBackButton() {
    if (this.state.orgChoiceMode === "None" || this.props.preview === "auto") {
      return (
        <Button
          className="back-inner-button"
          type="text"
          size="large"
          icon={<ArrowLeftOutlined />}
          onClick={() => history.back()}
        ></Button>
      );
    }
  }

  render() {
    const application = this.getApplicationObj();
    if (application === undefined) {
      return null;
    }
    if (application === null) {
      return Util.renderMessageLarge(this, this.state.msg);
    }

    if (this.state.localCallbackSent) {
      return (
        <Row justify="center" align="middle" style={{minHeight: "60vh"}}>
          <Col style={{textAlign: "center"}}>
            <CheckCircleFilled style={{fontSize: 64, color: "#52c41a"}} />
            <p style={{fontSize: 20, marginTop: 24, marginBottom: 24}}>
              {i18next.t("general:Login successful")}
            </p>
            <Button type="primary" onClick={() => window.close()}>
              {i18next.t("general:Click to return to")} {this.state.localCallbackAppName}
            </Button>
          </Col>
        </Row>
      );
    }

    if (this.state.samlResponse !== "") {
      return (
        <RedirectForm
          samlResponse={this.state.samlResponse}
          redirectUrl={this.state.redirectUrl}
          relayState={this.state.relayState}
        />
      );
    }

    if (application.signinHtml !== "") {
      return (
        <div dangerouslySetInnerHTML={{ __html: application.signinHtml }} />
      );
    }

    const visibleOAuthProviderItems =
      application.providers === null
        ? []
        : application.providers.filter((providerItem) =>
            this.isProviderVisible(providerItem)
          );
    if (
      this.props.preview !== "auto" &&
      !Setting.isPasswordEnabled(application) &&
      !Setting.isCodeSigninEnabled(application) &&
      !Setting.isWebAuthnEnabled(application) &&
      !Setting.isLdapEnabled(application) &&
      visibleOAuthProviderItems.length === 1
    ) {
      Setting.goToLink(
        Provider.getAuthUrl(
          application,
          visibleOAuthProviderItems[0].provider,
          "signup"
        )
      );
      return (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Spin size="large" tip={i18next.t("login:Signing in...")} />
        </div>
      );
    }

    if (document.querySelector("video")) {
      document.querySelector("video").playbackRate = 0.8;
    }

    const language = Setting.getAcceptLanguage().split(";")[0];
    return (
      <>
        <CustomGithubCorner />
        {this.state.updateState && (
          <div
            className="loginUpdateNotice"
            style={{
              backgroundImage: `url(${require("../assets/forget_bg.png")})`,
              width: "100%",
              height: "calc(100% - 36px)",
              position: "absolute",
              zIndex: 11,
              top: 0,
            }}
          >
            {this.renderModal()}
          </div>
        )}
        {!this.state.updateState && (
          <div className="loginPageBox">
            {
              !Setting.isMobile() && (
                <video
                  className="login-video"
                  preload="metadata"
                  autoPlay
                  loop
                  muted
                >
                  <source src={`${OssUrl}/bg.mp4`} type="video/mp4" />
                  <object data={`${OssUrl}/bg.mp4`}>
                    <embed src={`${OssUrl}/bg.mp4`} type="video/mp4" />
                  </object>
                </video>
              )
              // <div className="login-video" style={{backgroundImage:`url(${OssUrl}/bg1.png)`}}/>
            }
            <div className="login-container">
              <div className="login-slogan">
                <div>
                  <div className={`slogan-title ${language}`}>
                    {i18next.t(
                      "login:Defining A New Model Of Inclusive Computing Power"
                    )}
                    <br />
                    {i18next.t("login:Of Inclusive Computing Power")}
                  </div>
                  <div className="slogan-subtitle">
                    <div>
                      <span className={`slogan-subtitle-text-${language}`}>
                        {i18next.t("login:First to Consume by")}
                      </span>
                      <span className="slogan-subtitle-dcu">
                        "{i18next.t("login:DCU")}"
                      </span>
                      {i18next.t("login:Consume")}
                    </div>
                    <div>
                      <span className={`slogan-subtitle-text-${language}`}>
                        {i18next.t("login:Idle")}
                      </span>
                      <span className="slogan-subtitle-zero">0</span>
                      <span className={`slogan-subtitle-text-${language}`}>
                        {i18next.t("login:Billing")}
                      </span>
                    </div>
                    <div>
                      {i18next.t("login:Power")}
                      <span className="slogan-subtitle-zero">0</span>
                      <span className={`slogan-subtitle-text-${language}`}>
                        {i18next.t("login:Wasted Computing Power")}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="login-content login-custom">
                {Setting.inIframe() || Setting.isMobile() ? null : (
                  <div
                    dangerouslySetInnerHTML={{
                      __html: customDict.formCss || application.formCss,
                    }}
                  />
                )}
                {Setting.inIframe() || !Setting.isMobile() ? null : (
                  <div
                    dangerouslySetInnerHTML={{
                      __html: application.formCssMobile,
                    }}
                  />
                )}
                <div className="login-panel">
                  {customDict.isShowVideo && (
                    <div
                      className="side-image"
                      style={{
                        display: application.formOffset !== 4 ? "none" : null,
                      }}
                    >
                      <div
                        dangerouslySetInnerHTML={{
                          __html: application.formSideHtml,
                        }}
                      />
                    </div>
                  )}
                  <div className="login-form">
                    <div>{this.renderLoginPanel(application)}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }
}

export default withRouter(LoginPage);
